import { INode } from 'shared/types/tree'
import {
  CourseSummaryMapping,
  nodeSearchResult,
  PathData,
  SummaryCSVData,
  SummaryData,
  SummaryMapping,
} from './types'
import { nodesOfTypeWithContent } from '../../utility/TreeHelpers'
import { NodeType } from 'shared/types/ingestion'
import { retrieveIds, updateTreeNode } from '../TreeRequests'

export function nodeHasMapping(
  node: INode,
  summaryType: 'long' | 'short',
  summary: string,
) {
  return node.body?.summary?.[summaryType] === summary
}

export function addNodeSummary(node: INode, summaryData: SummaryData[]) {
  const body = summaryData.reduce<{
    summary: { long?: string; short?: string }
  }>(
    ({ summary, ...rest }, { summaryType, generations }) => {
      return {
        ...rest,
        summary: {
          ...summary,
          [summaryType]: generations,
        },
      }
    },
    {
      ...node.body,
      summary: {
        ...node.body?.summary,
      },
    },
  )
  return updateTreeNode({ ...retrieveIds(node), body })
}

function getSummaryType(summaryTypeId: string) {
  if (summaryTypeId.includes('315')) return 'long'
  return 'short'
}

export function summaryDataByMasterId(
  headers: string[],
  datas: SummaryCSVData[],
) {
  const typePath = getTypePath(headers)
  return datas.reduce<Record<string, CourseSummaryMapping>>((acc, data) => {
    const { curriculumTitle, summaryLevel, generations, summary, treeId } = data
    if (!(treeId in acc)) {
      acc[treeId] = {
        curriculumTitle,
        treeId,
        summaryData: [],
      }
    }
    const pathData = typePath.reduce<{ nodeType: string; nodeTitle: string }[]>(
      (acc, nodeType) => {
        const nodeTitle = data[`${nodeType}Title`]
        if (nodeTitle) {
          acc.push({ nodeType, nodeTitle })
        }
        return acc
      },
      [],
    )

    acc[treeId].summaryData.push({
      summaryLevel,
      pathData,
      generations,
      summaryType: getSummaryType(summary),
    })
    return acc
  }, {})
}

function getTypePath(headers: string[]) {
  return headers.reduce<string[]>((acc, header) => {
    if (header.includes('Title') && !header.startsWith('curriculum')) {
      acc.push(header.replace('Title', ''))
    }
    return acc
  }, [])
}

function nodeForTypeTitlePath(tree: INode, pathData: PathData[]) {
  const titleCheck = (title: string) => (node: INode) =>
    node.title.trim() === title.trim()

  return pathData.reduce<nodeSearchResult>(
    ({ node, pathResult }, { nodeType, nodeTitle }) => {
      if (!node) return { node, pathResult }
      const type = nodeType === 'lesson' ? 'lesson_plan' : nodeType
      const nodes = nodesOfTypeWithContent({
        tree: node,
        types: [type, NodeType.Document],
        extraCheck: titleCheck(nodeTitle),
      })
      if (nodes.length > 1) {
        throw new Error('Unexpected situation')
      }
      return {
        node: nodes[0],
        pathResult: [
          ...pathResult,
          {
            nodeType,
            nodeTitle,
            found: nodes[0]?.id,
          },
        ],
      }
    },
    { node: tree, pathResult: [] },
  )
}

export function treeSummaryMappings(
  summaries: CourseSummaryMapping,
  tree: INode,
) {
  const { summaryData } = summaries

  return summaryData.reduce<SummaryMapping>(
    (acc, summary) => {
      const { node, pathResult } = nodeForTypeTitlePath(tree, summary.pathData)
      if (node) {
        if (nodeHasMapping(node, summary.summaryType, summary.generations)) {
          acc.done = {
            ...acc.done,
            [node.id]: [...(acc.done[node.id] ?? []), summary],
          }
        } else {
          acc.mapped = {
            ...acc.mapped,
            [node.id]: [...(acc.mapped[node.id] ?? []), summary],
          }
        }
      } else {
        acc.unMapped.push({ summary, pathResult })
      }
      return acc
    },
    {
      mapped: {},
      unMapped: [],
      done: {},
    },
  )
}
